/**
 * Convierte un objeto anidado en un objeto plano.
 * @param obj Objeto anidado.
 * @param prefix Prefijo para las claves.
 * @param res Objeto plano.
 * @returns Objeto plano.
 */
export function aplanarObj(obj: any, prefix = '', res: any = {}) {
    for (const key in obj) {
        if (!obj.hasOwnProperty(key)) continue;
        const value = obj[key];
        if (value === null || value === undefined) continue;
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (value && typeof value === 'object' && !(value instanceof Date)) {
            aplanarObj(value, newKey, res);
        } else {
            res[newKey] = value;
        }
    }
    return res;
}

/**
 * Convierte un objeto plano en un objeto anidado.
 * @param obj Objeto plano.
 * @returns Objeto anidado.
 */
export function desaplanarObj(obj: Record<string, any>): any {
    const result: any = {};
    for (const flatKey in obj) {
        if (!obj.hasOwnProperty(flatKey)) continue;
        const keys = flatKey.split('.');
        let current = result;
        keys.forEach((key, idx) => {
            if (idx === keys.length - 1) {
                // Si el valor es un objeto plano con claves aplanadas, lo desaplana recursivamente
                if (typeof obj[flatKey] === 'object' && obj[flatKey] !== null && !Array.isArray(obj[flatKey]) && Object.keys(obj[flatKey]).some((k) => k.includes('.'))) {
                    current[key] = desaplanarObj(obj[flatKey]);
                } else {
                    current[key] = obj[flatKey];
                }
            } else {
                if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
                    current[key] = {};
                }
                current = current[key];
            }
        });
    }
    return result;
}

/**
 * Compara dos objetos para verificar si son iguales.
 * @param obj1 Objeto 1.
 * @param obj2 Objeto 2.
 * @returns true si son iguales, false en caso contrario.
 */
export function sonObjetosIguales(obj1: any, obj2: any) {
  // Comparar referencias
  if (obj1 === obj2) return true;
  
  // Verificar si ambos son objetos
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;
  if (obj1 === null || obj2 === null) return false;
  
  // Comparar claves
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  // Comparar valores
  for (let key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!sonObjetosIguales(obj1[key], obj2[key])) return false;
  }
  
  return true;
}

/**
 * Intenta convertir una cadena de texto en un objeto JSON.
 * 
 * Propósito:
 * Esta función se utiliza para validar y convertir cadenas que se espera que sean objetos JSON.
 * Es útil cuando se reciben datos de fuentes externas (como localStorage o APIs) y se desea
 * asegurar que el contenido sea un objeto con datos antes de procesarlo.
 * 
 * Flujo de código:
 * 1. Utiliza un bloque try-catch para manejar errores de parseo de JSON.
 * 2. Si el parseo es exitoso, verifica que el resultado sea un objeto no nulo y no sea un tipo primitivo.
 * 3. Comprueba si el objeto tiene propiedades (no es un objeto vacío '{}' o un array vacío '[]').
 * 4. Si todas las condiciones se cumplen, devuelve el objeto; de lo contrario, devuelve null.
 * 
 * @param cadena - La cadena de texto que se desea parsear.
 * @returns El objeto parseado si es un JSON válido y no está vacío, o null en caso contrario.
 */
export function obtenerObjDeJSON(cadena: string): any {
  try {
    if (!cadena || typeof cadena !== 'string') {
      return null;
    }

    const resultado = JSON.parse(cadena);
    
    // Verificar que sea un objeto (incluye arrays) y no sea null
    if (typeof resultado === 'object' && resultado !== null) {
      // Si es un objeto o array, verificar que tenga contenido (no sea {} o [])
      if (Object.keys(resultado).length > 0) {
        return resultado;
      }
    }
    
    // Si es un JSON válido pero es un tipo primitivo (string, number, boolean) o está vacío
    return null;
  } catch (error) {
    // Si no es un JSON válido, capturamos el error y devolvemos null
    return null;
  }
}


